import { useNavigate } from "react-router-dom";
import { ResponsiveContainer, AreaChart, Area, XAxis, YAxis, Tooltip, ReferenceLine, CartesianGrid } from "recharts";
import { brl, pct, dm, weekLabel, lastPeriodLabel } from "@/lib/format";
import { Card } from "@/components/kit";

export function LastWeekCard({ last, target }) {
  const over = last.pct !== null && last.pct > target;
  return (
    <div className="card lg:col-span-1 lg:self-start flex flex-col justify-between text-white" style={{ background: "var(--brand)", borderColor: "var(--brand)" }} data-testid="last-week-card">
      <div className="eyebrow" style={{ color: "rgba(255,255,255,0.75)" }}>{lastPeriodLabel(last)} · {dm(last.week_start)} a {dm(last.week_end)}</div>
      <div className="flex items-end gap-3 mt-4">
        <span className="text-5xl font-extrabold tnum" data-testid="last-week-cmv-pct">{pct(last.pct)}</span>
        <span className="w-3 h-3 rounded-full mb-3" style={{ background: over ? "var(--accent)" : "#9fd3b0" }} />
      </div>
      <div className="text-sm mt-1" style={{ opacity: 0.8 }}>CMV sobre faturamento · meta {pct(target, 0)}</div>
      <div className="grid grid-cols-2 gap-3 mt-6 pt-4 border-t" style={{ borderColor: "rgba(255,255,255,0.2)" }}>
        <div><div className="text-xs opacity-75">Faturamento</div><div className="h3 tnum" data-testid="last-week-revenue">{brl(last.revenue)}</div></div>
        <div><div className="text-xs opacity-75">CMV</div><div className="h3 tnum" data-testid="last-week-cmv">{brl(last.cmv)}</div></div>
      </div>
    </div>
  );
}

export function Kpi({ label, value, testid }) {
  return (
    <div className="card" data-testid={testid}>
      <div className="eyebrow">{label}</div>
      <div className="h2 tnum mt-2">{value}</div>
    </div>
  );
}

export function EvolutionChart({ weeks, target }) {
  const chart = weeks.map((w) => ({ name: dm(w.week_start), pct: w.pct ?? 0 }));
  return (
    <Card data-testid="cmv-evolution-chart">
      <div className="h3">Evolução do CMV (%)</div>
      <div className="caption">Linha tracejada = meta de {pct(target, 0)}</div>
      {chart.length < 2 ? (
        <p className="caption mt-6">Registre ao menos 2 conferências para ver a evolução.</p>
      ) : (
        <div className="h-56 mt-4">
          <ResponsiveContainer width="100%" height="100%">
            <AreaChart data={chart} margin={{ left: -18, right: 8, top: 8 }}>
              <defs><linearGradient id="g" x1="0" y1="0" x2="0" y2="1"><stop offset="0%" stopColor="var(--brand)" stopOpacity={0.35} /><stop offset="100%" stopColor="var(--brand)" stopOpacity={0} /></linearGradient></defs>
              <CartesianGrid stroke="var(--border)" vertical={false} />
              <XAxis dataKey="name" tick={{ fontSize: 11, fill: "var(--muted)" }} axisLine={false} tickLine={false} />
              <YAxis tick={{ fontSize: 11, fill: "var(--muted)" }} axisLine={false} tickLine={false} unit="%" />
              <Tooltip formatter={(v) => pct(v)} contentStyle={{ borderRadius: 10, border: "1px solid var(--border)", background: "var(--surface)" }} />
              <ReferenceLine y={target} stroke="var(--accent)" strokeDasharray="5 5" />
              <Area type="monotone" dataKey="pct" stroke="var(--brand)" strokeWidth={2.5} fill="url(#g)" dot={{ r: 3, fill: "var(--brand)" }} />
            </AreaChart>
          </ResponsiveContainer>
        </div>
      )}
    </Card>
  );
}

export function CategoryBreakdown({ cats }) {
  return (
    <Card data-testid="category-breakdown">
      <div className="h3 mb-4">CMV por categoria</div>
      {cats.length === 0 && <p className="caption">Sem dados no período.</p>}
      <div className="flex flex-col gap-4">
        {cats.map((c) => (
          <div key={c.category_id}>
            <div className="flex justify-between text-sm mb-1.5"><span className="font-semibold">{c.name}</span><span className="muted tnum">{brl(c.cmv)} · {pct(c.pct, 0)}</span></div>
            <div className="bar-track"><div className="bar-fill" style={{ width: `${Math.max(2, c.pct)}%` }} /></div>
          </div>
        ))}
      </div>
    </Card>
  );
}

export function WeeklyHistory({ weeks, target }) {
  const nav = useNavigate();
  return (
    <Card style={{ padding: 0 }} data-testid="weekly-history">
      <div className="h3" style={{ padding: "var(--pad) var(--pad) 8px" }}>Histórico de conferências</div>
      <div className="divide">
        {[...weeks].reverse().map((w) => (
          <div key={w.id} className="row-link" onClick={() => nav(`/conference/${w.id}`)} data-testid={`week-row-${w.id}`}>
            <div className="flex-1"><div className="label">{weekLabel(w)}</div><div className="caption">{brl(w.revenue)} faturamento · {brl(w.cmv)} CMV</div></div>
            <span className={`badge badge-${w.pct === null ? "muted" : w.pct > target ? "warning" : "success"}`}>{pct(w.pct)}</span>
          </div>
        ))}
      </div>
    </Card>
  );
}

export function periodTotals(weeks) {
  const rev = weeks.reduce((a, w) => a + w.revenue, 0);
  const cmv = weeks.reduce((a, w) => a + w.cmv, 0);
  const cats = {};
  weeks.forEach((w) => w.by_category.forEach((c) => { cats[c.category_id] = { ...c, cmv: (cats[c.category_id]?.cmv || 0) + c.cmv }; }));
  const list = Object.values(cats).sort((a, b) => b.cmv - a.cmv).map((c) => ({ ...c, pct: cmv ? (c.cmv / cmv) * 100 : 0 }));
  return { rev, cmv, pct: rev ? (cmv / rev) * 100 : null, cats: list };
}
